import { EXTEND, IS, IS_NOT, NO, UIRoute, UIView, UIViewBroadcastEvent, UIViewController, YES } from "uicore-ts"
import { RHCore } from "../Core/RHCore"
import { CBButtonGroup } from "../Custom components/CB/CBButtonGroup"
import { RHColor } from "../Custom components/RH/RHColor"
import { RHButton } from "../Custom components/RH/RHButton"
import { RHNotificationBadge } from "../Custom components/RH/RHNotificationBadge"
import { RHPanelView } from "../Custom components/RH/RHPanelView"
import { RHRadioButton } from "../Custom components/RH/RHRadioButton"
import { RHButtons, RHSpacing } from "../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHLogViewTokens } from "../Custom components/RH/Utils_slop/RHViewTokens"
import { EventLogViewController } from "./EventLogViewController"
import { NotificationsViewController } from "./NotificationsViewController"
import { ProcurementDetailsViewController } from "../ProcurementDetails/ProcurementDetailsViewController"


export class LogViewController extends UIViewController {

    static override readonly routeComponentName = "log"
    static override readonly ParameterIdentifierName = {
        tab: "tab",
        procurementId: "procurementId",
        referenceNumber: "referenceNumber",
        notificationId: "notificationId",
        notificationCompanyId: "notificationCompanyId",
        notificationApplicationMode: "notificationApplicationMode"
    }

    readonly titleKey = "log"

    readonly backButton = new RHButton().configuredWithObject({
        titleLabel: { text: "← Tagasi" },
        variant: "quietWarning",
        contentPadding: RHButtons.compactContentPadding,
        style: { fontSize: RHButtons.compactFontSize },
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: () => {
                if (this._notificationsViewController?.detailsView.isMemberOfViewTree) {
                    this._notificationsViewController.closeNotificationDetails()
                    UIRoute.currentRoute
                        .routeByRemovingParameterInComponent(LogViewController.routeComponentName, LogViewController.ParameterIdentifierName.notificationId)
                        .routeByRemovingParameterInComponent(LogViewController.routeComponentName, LogViewController.ParameterIdentifierName.notificationCompanyId)
                        .routeByRemovingParameterInComponent(LogViewController.routeComponentName, LogViewController.ParameterIdentifierName.notificationApplicationMode)
                        .applyByReplacingCurrentRouteInHistoryWithoutRouteChangeApproval()
                }
                else if (this._procurementIdToReturnTo) {
                    UIRoute.currentRoute.routeByRemovingComponentNamed(LogViewController.routeComponentName)
                        .routeWithViewControllerComponent(ProcurementDetailsViewController, {
                            id: this._procurementIdToReturnTo, tab: "details"
                        }).apply()
                }
            }
        }
    })

    // ── Section tabs ──────────────────────────────────────────────────────────

    readonly notificationsTabButton = new RHRadioButton().addedAsSubviewToView(this.view).configuredWithObject({
        titleLabel: { text: "Minu teatised" }
    })

    readonly myEventsTabButton = new RHRadioButton().addedAsSubviewToView(this.view).configuredWithObject({
        titleLabel: { text: "Minu tegevused" }
    })

    readonly sectionTabGroup = new CBButtonGroup({
        "notifications": this.notificationsTabButton,
        "eventLog":      this.myEventsTabButton
    }).configuredWithObject({
        value: "notifications",
        selectionDidChange: (_button, value) => this._didSelectTab(value ?? "notifications")
    })

    // ── Panel ─────────────────────────────────────────────────────────────────

    readonly contentPanelView = new RHPanelView().addedAsSubviewToView(this.view).configuredWithObject({
        layoutSubviews: EXTEND(() => {
            if (this.contentPanelView.subviews.firstElement) {
                this.contentPanelView.subviews.firstElement.frame = this.contentPanelView.bounds.rectangleWithHeight(
                    this.contentPanelView.subviews.firstElement
                )
            }
        })
    })

    // ── Child view controllers ────────────────────────────────────────────────

    private _notificationsViewController: NotificationsViewController | undefined
    private _eventLogViewController: EventLogViewController | undefined
    _procurementIdToReturnTo?: string

    private get notificationsViewController(): NotificationsViewController {
        if (IS_NOT(this._notificationsViewController)) {
            this._notificationsViewController = new NotificationsViewController(
                new UIView("NotificationsView")
            )
            this._notificationsViewController.detailsVisibilityDidChange = isShowingDetails => {
                this.backButton.titleLabel.text = "← Tagasi"
                if (!isShowingDetails && this._procurementIdToReturnTo) this.backButton.titleLabel.text = "← Tagasi hankesse"
                if (isShowingDetails || this._procurementIdToReturnTo) {
                    this.backButton.addedAsSubviewToView(this.view)
                }
                else {
                    this.backButton.removeFromSuperview()
                }
                this.view.setNeedsLayoutUpToRootView()
            }
        }
        return this._notificationsViewController
    }

    private get eventLogViewController(): EventLogViewController {
        if (IS_NOT(this._eventLogViewController)) {
            this._eventLogViewController = new EventLogViewController(
                new UIView("EventLogView")
            )
        }
        return this._eventLogViewController
    }

    // ── Constructor ───────────────────────────────────────────────────────────

    constructor(view: UIView) {
        super(view)
        this.view.configureWithObject({ backgroundColor: RHColor.background })
    }

    // ── Lifecycle ─────────────────────────────────────────────────────────────

    override async viewDidAppear() {
        await super.viewDidAppear()
        // Read-only nav badge refresh + unread-message map reload. Also picks up
        // purely time-based count changes (deadline crossings) no push covers —
        // the same on-entry pair every main list view calls.
        RHCore.sharedInstance.notificationStore.refreshGlobalCounts()
        RHCore.sharedInstance.notificationStore.reloadOfferMessageCounts()
        this._updateTabBadges()
    }

    // ── Tab selection ─────────────────────────────────────────────────────────

    private _didSelectTab(value: string) {
        UIRoute.currentRoute
            .routeWithComponent(
                LogViewController.routeComponentName,
                { [LogViewController.ParameterIdentifierName.tab]: value },
                YES
            )
            .apply()
        this.contentPanelView.setNeedsLayoutUpToRootView()
    }

    // ── Routing ───────────────────────────────────────────────────────────────

    override async handleRoute(route: UIRoute) {
        await super.handleRoute(route)

        const requestedTabValue = route.componentWithViewController(LogViewController)
                ?.parameters[LogViewController.ParameterIdentifierName.tab]
            ?? "notifications"

        let tabValue = "notifications"
        if (requestedTabValue === "eventLog") {
            tabValue = "eventLog"
        }
        this._procurementIdToReturnTo = route.componentWithViewController(LogViewController)
            ?.parameters[LogViewController.ParameterIdentifierName.procurementId]

        this.sectionTabGroup.value = tabValue

        if (tabValue === "eventLog") {
            this.backButton.removeFromSuperview()
            if (!this.hasChildViewController(this.eventLogViewController)) {
                if (IS(this._notificationsViewController) && this.hasChildViewController(this._notificationsViewController)) {
                    this.removeChildViewController(this._notificationsViewController)
                }
                this.addChildViewControllerInContainer(this.eventLogViewController, this.contentPanelView)
            }
            else {
                // The cached child does not receive viewDidAppear again when the
                // parent route is re-applied, so refresh both company scope and rows.
                void this.eventLogViewController.reloadEvents()
            }
        }
        else {
            await this.notificationsViewController.handleRoute(route)
            if (!route.componentWithViewController(LogViewController)?.parameters.notificationId) {
                this.notificationsViewController.closeNotificationDetails()
            }
            if (!this.hasChildViewController(this.notificationsViewController)) {
                if (IS(this._eventLogViewController) && this.hasChildViewController(this._eventLogViewController)) {
                    this.removeChildViewController(this._eventLogViewController)
                }
                this.addChildViewControllerInContainer(this.notificationsViewController, this.contentPanelView)
            }
            else {
                // Already attached — returning here via SPA navigation does NOT
                // re-fire the child's viewDidAppear, so reload explicitly. Without
                // this the per-row bold state keeps showing the previous visit's
                // state until a hard refresh.
                void this.notificationsViewController.reloadNotifications()
            }
        }

        this.view.setNeedsLayoutUpToRootView()
        this._updateTabBadges()
    }

    // ── Layout ────────────────────────────────────────────────────────────────

    override viewDidReceiveBroadcastEvent(event: UIViewBroadcastEvent) {
        super.viewDidReceiveBroadcastEvent(event)
        if (event.name === RHCore.broadcastEventName.applicationModeDidChange) {
            this._updateTabBadges()
            this.view.setNeedsLayout()
        }
        else if (event.name === RHCore.broadcastEventName.unseenEventCountDidChange) {
            this._updateTabBadges()
        }
    }

    /** Applies unread emphasis only to the notification inbox tab. */
    private _updateTabBadges() {
        const counts = RHCore.sharedInstance.unseenCounts
        RHNotificationBadge.applyUnreadEmphasis(
            this.notificationsTabButton.titleLabel,
            counts.totalEvents > 0,
            "Lugemata teavitused."
        )
        RHNotificationBadge.applyUnreadEmphasis(this.myEventsTabButton.titleLabel, NO)
    }

    override layoutViewSubviews() {
        super.layoutViewSubviews()

        const padding = this.core.paddingLength
        const labelHeight = padding * 2
        const bounds = this.view.contentBoundsWithInset(padding)

        const backButton = this.backButton
        const tabBarHeight = labelHeight * 2.5

        let tabButtonsWidth = RHLogViewTokens.tabButtonWidth * 2
            + RHSpacing.xLarge
        if (tabButtonsWidth > bounds.width) {
            tabButtonsWidth = bounds.width
        }
        const needsSeparateBackButtonRow = bounds.width <
            tabButtonsWidth + (RHButtons.pageBackButtonWidth + padding) * 2
        const tabBarFrame = bounds.rectangleWithHeight(tabBarHeight)
            .IF(backButton.superview === this.view)
            .IF(needsSeparateBackButtonRow)
            .rectangleWithHeight(RHButtons.compactHeight)
            .ENDIF()
            .performingFunctionWithSelf(frame => {
                frame.rectangleWithMaxWidth(1180, 0.5)
                    .rectangleWithWidth(RHButtons.pageBackButtonWidth)
                    .rectangleWithHeight(RHButtons.compactHeight, 0.5)
                    .assignedAsFrameOfView(backButton)
            })
            .IF(needsSeparateBackButtonRow)
            .rectangleForNextRow(padding, tabBarHeight)
            .ENDIF()
            .ENDIF()

        tabBarFrame
            .rectangleWithWidth(tabButtonsWidth, 0.5)
            .distributeViewsEquallyAlongWidth(
                [this.notificationsTabButton, this.myEventsTabButton],
                RHSpacing.xLarge
            )

        // These views carry little per-row content, so the panel is narrowed to
        // the new-procurement form width and centred for visual unity.
        tabBarFrame
            .rectangleForNextRow(padding, 0)
            .rectangleWithMaxWidth(1180, 0.5)
            .rectangleWithHeight(this.contentPanelView)
            .assignedAsFrameOfView(this.contentPanelView)
    }

}
